import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../../../../app/hooks';
import { useAskAica } from '../../../../../../../store/dashboard/askAica';
import { selectViewOnly } from '../../../../../../../store/dashboard/askAica/selectors';
import { setAskAicaSelectedSources } from '../../../../../../../store/dashboard/askAica/actions';
import { useAppDispatch } from '../../../../../../../app/hooks';

import { AskAicaSurfaceProvider, AskAicaSurfaceContextValue, ASK_AICA_SURFACE } from './AskAicaSurfaceContext';

/**
 * Thin passthrough of useAskAica() + the leads-only slice reads (isClearingChat,
 * selectViewOnly, activeOrgId) onto the shared surface context. This is the SAME
 * data path AskAicaV2/LeftPanel/MessagesContainer/ReasoningPanel/AskAicaInputBox
 * read today — only the delivery mechanism changes (useAppSelector → useContext).
 * MUST NOT change any leads behaviour (RECUR-27369 §2/§4).
 */
export const LeadsAskAicaSurfaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const {
        messages,
        reasoning,
        isLoading,
        error,
        sendMessage,
        clearChatMemory,
        registerStreamStartedCallback,
        isLoadingChatHistory,
        messagesEndRef,
        fetchChatHistory,
        fetchLibraryFiles,
        fetchOrgConversations,
        hasMoreData,
    } = useAskAica();

    const isClearingChat = useAppSelector((state) => state.dashboard.askAicaReducer.isClearingChat);
    const isViewOnly = useAppSelector(selectViewOnly);
    const selectedSources = useAppSelector((state) => state.dashboard.askAicaReducer.uiState.selectedSources);
    const conversations = useAppSelector((state) => state.dashboard.askAicaReducer.conversations);
    const isLoadingConversations = useAppSelector((state) => state.dashboard.askAicaReducer.isLoadingConversations);
    const selectedConversationId = useAppSelector((state) => state.dashboard.askAicaReducer.selectedConversationId);
    const activeOrgId = useAppSelector((state) => state.dashboard.askAicaReducer.activeOrgId);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    // Mirrors useAskAica.ts's own org_id = params.id ?? investorOrgId resolution
    // (useAskAica doesn't expose that value itself).
    const fileContextOrgId = params.id ? params.id : investorOrgId;

    const value: AskAicaSurfaceContextValue = useMemo(
        () => ({
            surface: ASK_AICA_SURFACE.LEADS,
            messages,
            reasoning,
            isLoading,
            isLoadingChatHistory,
            error,
            hasMoreData,
            selectedSources,
            setSelectedSources: (sources: string[]) => dispatch(setAskAicaSelectedSources(sources)),
            sendMessage: (content: string, flowKey?: string, agentInput?: any) => sendMessage(content, flowKey, agentInput),
            clearChat: clearChatMemory,
            fetchChatHistory,
            fetchOrgConversations,
            fetchLibraryFiles,
            registerStreamStartedCallback,
            messagesEndRef,
            fileContextOrgId,
            conversations,
            isLoadingConversations,
            selectedConversationId,
            isClearingChat,
            isViewOnly,
            activeOrgId,
            enableFeedbackMention: true,
        }),
        [
            messages,
            reasoning,
            isLoading,
            isLoadingChatHistory,
            error,
            hasMoreData,
            selectedSources,
            dispatch,
            sendMessage,
            clearChatMemory,
            fetchChatHistory,
            fetchOrgConversations,
            fetchLibraryFiles,
            registerStreamStartedCallback,
            messagesEndRef,
            fileContextOrgId,
            conversations,
            isLoadingConversations,
            selectedConversationId,
            isClearingChat,
            isViewOnly,
            activeOrgId,
        ],
    );

    return <AskAicaSurfaceProvider value={value}>{children}</AskAicaSurfaceProvider>;
};

export default LeadsAskAicaSurfaceProvider;
